import React, { useState, useEffect, useRef } from 'react';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent } from './ui/card';
import { cn } from '../lib/utils';
import { Feature } from '../lib/types';
import { Button } from './ui/button';

interface FeatureSelectionProps {
  onFeaturesChange: (selectedFeatures: Feature[]) => void;
  initialSelectedFeatures?: Feature[];
}

// General features
const GENERAL_FEATURES: Feature[] = [
  {
    id: 'global-setup',
    name: 'Global Setup',
    description: 'Header, footer, typographic styles, colors, localhost and staging environment setup.',
    minHours: 12,
    maxHours: 12,
  },
  {
    id: 'search-functionality-tier1',
    name: 'Basic Search Functionality',
    description: 'Basic Out of the Box search capabilities. Search bar, search results page in a grid.',
    minHours: 3,
    maxHours: 4,
  },
  {
    id: 'search-functionality-tier2',
    name: 'Advanced Search Functionality',
    description: 'Advanced Site-wide search capabilities. Search bar, search results page, and search results grid with filters.',
    minHours: 6,
    maxHours: 8,
  },
  {
    id: 'search-functionality-tier3',
    name: 'Robust Search Functionality',
    description: 'Robust Site-wide search capabilities. Search bar, search results page, predictable live search, and search results grid with filters.',
    minHours: 8,
    maxHours: 12,
  },
  // Animation subsection
  {
    id: 'animation-tier1',
    name: 'Basic Animations',
    description: 'Basic content fade in on scroll. Very simple but elegant.',
    minHours: 4,
    maxHours: 4,
    subsection: 'Animation',
  },
  {
    id: 'animation-tier2',
    name: 'Intermediate Animations',
    description: 'More advanced animations, such as parallax effects or pinned interactive elements.',
    minHours: 8,
    maxHours: 12,
    subsection: 'Animation',
  },
  {
    id: 'animation-tier3',
    name: 'Advanced Animations',
    description: 'Complex interactive animations and micro-interactions.',
    minHours: 12,
    maxHours: 16,
    subsection: 'Animation',
  },
  {
    id: 'animation-external',
    name: 'External Animations',
    description: 'Third party animations, hiring a freelancer to create animations for your website.',
    minHours: 5,
    maxHours: 20,
    subsection: 'Animation',
  },
  // Map Subsection
  {
    id: 'map-tier1',
    name: 'Static Illustrated Map',
    description: 'Static illustrated map with markers and basic interactions. No interactivity, no animations.',
    minHours: 2,
    maxHours: 2,
    subsection: 'Map',
  },
  {
    id: 'map-tier2',
    name: 'Interactive Illustrated Map',
    description: 'Interactive illustrated map with markers and basic interactions.',
    minHours: 4,
    maxHours: 8,
    subsection: 'Map',
  },
  {
    id: 'map-tier3',
    name: 'Interactive Google Map',
    description: 'Interactive Google Map with markers, filtering, sorting, and other basic interactions.',
    minHours: 8,
    maxHours: 12,
    subsection: 'Map',
  },
  // Real Estate features
  {
    id: 'property-api-import',
    name: 'Property Property or Floorplan API Import',
    description: 'Importing property data from an API.',
    minHours: 3,
    maxHours: 4,
    subsection: 'Real Estate'
  },
  {
    id: 'reoccuring-api-import',
    name: 'Reoccuring Property or Floorplan API Import',
    description: 'Scheduled automated data import from an API (updated listing status, price, etc.).',
    minHours: 3,
    maxHours: 4,
    subsection: 'Real Estate'
  },
  {
    id: 'form-integration-crm',
    name: 'Form Integration with CRM',
    description: 'Integrating forms with a CRM. (Passing property data to the CRM, etc.)',
    minHours: 3,
    maxHours: 4,
    subsection: 'Real Estate'
  },
  // Hospitality Features
  {
    id: 'hotel-booking-system',
    name: 'Hotel Booking System',
    description: 'Embeddable Hotel booking system for bookings and reservations. (E.g: Mews)',
    minHours: 3,
    maxHours: 4,
    subsection: 'Hospitality'
  },
  {
    id: 'external-hotel-booking-system',
    name: 'External Hotel Booking System',
    description: 'External hotel booking system for bookings and reservations via URL Parameters. Passes property data to the booking system. (E.g: Synxis)',
    minHours: 3,
    maxHours: 4,
    subsection: 'Hospitality'
  },
  {
    id: 'restaurant-booking-system',
    name: 'Restaurant Booking System',
    description: 'Restaurant booking system for reservations. (E.g: OpenTable, Resy, etc.)',
    minHours: 1,
    maxHours: 2,
    subsection: 'Hospitality'
  },
  {
    id: 'restaurant-menus',
    name: 'Restaurant Menus',
    description: 'PDF Restaurant menus with pricing.',
    minHours: 1,
    maxHours: 1,
    subsection: 'Hospitality'
  },
  {
    id: 'dynamic-restaurant-menus',
    name: 'Dynamic Restaurant Menus',
    description: 'CMS Editable restaurant menus with pricing.',
    minHours: 3,
    maxHours: 4,
    subsection: 'Hospitality'
  },
  // Events
  {
    id: 'event-calendar',
    name: 'Event Calendar',
    description: 'Event calendar with calendar or list view.',
    minHours: 6,
    maxHours: 8,
    subsection: 'Events'
  },
  {
    id: 'event-booking-system',
    name: 'Event Booking System',
    description: 'Third party event booking system.',
    minHours: 6,
    maxHours: 8,
    subsection: 'Events'
  },
  // Forms
  {
    id: 'contact-form',
    name: 'Contact Form',
    description: 'Contact form.',
    minHours: 2,
    maxHours: 2,
    subsection: 'Forms'
  },
  {
    id: 'newsletter-form',
    name: 'Newsletter Form',
    description: 'Newsletter form.',
    minHours: 2,
    maxHours: 2,
    subsection: 'Forms'
  },
  {
    id: 'blog-functionality',
    name: 'Basic Blog Functionality',
    description: 'Blog functionality with blog posts, blog categories, and blog tags.',
    minHours: 6,
    maxHours: 8,
    subsection: 'Blog & News'
  },
  {
    id: 'careers-listing',
    name: 'Careers Listing',
    description: 'Basic Careers listing with position listings that link offsite.',
    minHours: 3,
    maxHours: 4,
    subsection: 'Careers'
  },
  {
    id: 'careers-single-position',
    name: 'Careers Single Position',
    description: 'Careers single position with job description, application form, and application form submission.',
    minHours: 3,
    maxHours: 4,
    subsection: 'Careers'
  }
];

// E-Commerce features
const ECOMMERCE_FEATURES: Feature[] = [
  {
    id: 'membership-functionality',
    name: 'Membership Functionality',
    description: 'Membership functionality with membership tiers, membership benefits, and membership signup.',
    minHours: 6,
    maxHours: 8,
    subsection: 'Membership'
  },
  {
    id: 'wholesale-functionality',
    name: 'Wholesale Functionality',
    description: 'Wholesale functionality with wholesale tiers, wholesale benefits, and wholesale signup.',
    minHours: 6,
    maxHours: 8,
    subsection: 'Membership'
  },
  {
    id: 'bundle-discounts',
    name: 'Bundle Discounts',
    description: 'Bundle discounts for products.',
    minHours: 4,
    maxHours: 8,
    subsection: 'Bundles'
  },
  {
    id: 'gift-messaging',
    name: 'Gift Messaging',
    description: 'Gift messaging for products.',
    minHours: 1,
    maxHours: 2,
    subsection: 'Gifting'
  },
  {
    id: 'gift-cards',
    name: 'Gift Cards',
    description: 'Gift cards for products.',
    minHours: 1,
    maxHours: 2,
    subsection: 'Gifting'
  },
  {
    id: 'gift-shipping',
    name: 'Gift Shipping',
    description: 'Gift shipping for products.',
    minHours: 3,
    maxHours: 4,
    subsection: 'Gifting'
  },
  {
    id: 'customizable-products',
    name: 'Customizable Products',
    description: 'Customizable products with options and pricing.',
    minHours: 8,
    maxHours: 48,
    subsection: 'Product Details'
  }
];

type TabType = 'general' | 'ecommerce';

// Custom feature interface for the form
interface CustomFeatureForm {
  title: string;
  description: string;
  hours: number;
}

const FeatureSelection: React.FC<FeatureSelectionProps> = ({ onFeaturesChange, initialSelectedFeatures = [] }) => {
  // Use a ref to track if this is the first render
  const isFirstRender = useRef(true);
  
  // Use a ref to track the previous selected features
  const prevSelectedFeaturesRef = useRef<string[]>([]);
  
  const [activeTab, setActiveTab] = useState<TabType>('general');
  
  // Extract initial selected feature IDs
  const initialGeneralIds = initialSelectedFeatures
    .filter(f => GENERAL_FEATURES.some(gf => gf.id === f.id))
    .map(f => f.id);
  
  const initialEcommerceIds = initialSelectedFeatures
    .filter(f => ECOMMERCE_FEATURES.some(ef => ef.id === f.id))
    .map(f => f.id);
  
  // Extract initial custom features
  const initialCustoms = initialSelectedFeatures.filter(f => 
    f && f.id && (f.id.startsWith('custom-feature-') || f.subsection === 'Custom Features')
  );
  
  // State for selected feature IDs
  const [selectedGeneralIds, setSelectedGeneralIds] = useState<string[]>(
    initialGeneralIds
  );
  const [selectedEcommerceIds, setSelectedEcommerceIds] = useState<string[]>(initialEcommerceIds);
  
  // State for custom features - ensure they're always marked as selected
  const [customFeatures, setCustomFeatures] = useState<Feature[]>(
    initialCustoms.map(feature => ({ ...feature, selected: true }))
  );
  
  // Form state
  const [customFeatureForm, setCustomFeatureForm] = useState<CustomFeatureForm>({
    title: '',
    description: '',
    hours: 4
  });
  const [showCustomFeatureForm, setShowCustomFeatureForm] = useState<boolean>(false);

  // When component mounts, call onFeaturesChange with initial features
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      
      // Get initial selected features
      const generalFeatures = GENERAL_FEATURES.filter(f => selectedGeneralIds.includes(f.id));
      const ecommerceFeatures = ECOMMERCE_FEATURES.filter(f => selectedEcommerceIds.includes(f.id));
      
      // Combine with custom features
      const allSelected = [
        ...generalFeatures,
        ...ecommerceFeatures,
        ...customFeatures.map(f => ({
          ...f,
          selected: true,
          subsection: 'Custom Features',
          category: f.category || 'Custom',
          complexity: f.complexity || 1
        }))
      ];
      
      // Update the parent component with initial features
      onFeaturesChange(allSelected);
    }
  }, []);  // Run only once on mount
  
  // Update selected features when initialSelectedFeatures changes
  useEffect(() => {
    console.log('initialSelectedFeatures changed:', initialSelectedFeatures.map(f => f.id));
    console.log('Current selectedGeneralIds:', selectedGeneralIds);
    console.log('Current selectedEcommerceIds:', selectedEcommerceIds);
    
    // Extract feature IDs from initialSelectedFeatures
    const newGeneralIds = initialSelectedFeatures
      .filter(f => GENERAL_FEATURES.some(gf => gf.id === f.id))
      .map(f => f.id);
    
    const newEcommerceIds = initialSelectedFeatures
      .filter(f => ECOMMERCE_FEATURES.some(ef => ef.id === f.id))
      .map(f => f.id);
    
    // Extract custom features
    const newCustoms = initialSelectedFeatures.filter(f => 
      f && f.id && (f.id.startsWith('custom-feature-') || f.subsection === 'Custom Features')
    );
    
    console.log('New generalIds:', newGeneralIds);
    console.log('New ecommerceIds:', newEcommerceIds);
    
    // Update state if the IDs have changed
    if (JSON.stringify(newGeneralIds) !== JSON.stringify(selectedGeneralIds)) {
      setSelectedGeneralIds(newGeneralIds);
    }
    
    if (JSON.stringify(newEcommerceIds) !== JSON.stringify(selectedEcommerceIds)) {
      setSelectedEcommerceIds(newEcommerceIds);
    }
    
    // Update custom features
    if (JSON.stringify(newCustoms.map(f => f.id)) !== JSON.stringify(customFeatures.map(f => f.id))) {
      setCustomFeatures(newCustoms.map(feature => ({ ...feature, selected: true })));
    }
  }, [initialSelectedFeatures]);  // Only depend on initialSelectedFeatures

  // Handle standard feature toggle
  const handleFeatureToggle = (featureId: string, isChecked: boolean) => {
    let updatedGeneralIds = [...selectedGeneralIds];
    let updatedEcommerceIds = [...selectedEcommerceIds];
    
    if (activeTab === 'general') {
      if (isChecked) {
        // Add to general features if not already included
        if (!updatedGeneralIds.includes(featureId)) {
          updatedGeneralIds.push(featureId);
        }
      } else {
        // Remove from general features
        updatedGeneralIds = updatedGeneralIds.filter(id => id !== featureId);
      }
      
      // Update state
      setSelectedGeneralIds(updatedGeneralIds);
    } else {
      if (isChecked) {
        // Add to ecommerce features if not already included
        if (!updatedEcommerceIds.includes(featureId)) {
          updatedEcommerceIds.push(featureId);
        }
      } else {
        // Remove from ecommerce features
        updatedEcommerceIds = updatedEcommerceIds.filter(id => id !== featureId);
      }
      
      // Update state
      setSelectedEcommerceIds(updatedEcommerceIds);
    }
    
    // Get all selected features
    const generalFeatures = GENERAL_FEATURES.filter(f => 
      activeTab === 'general' ? updatedGeneralIds.includes(f.id) : selectedGeneralIds.includes(f.id)
    );
    const ecommerceFeatures = ECOMMERCE_FEATURES.filter(f => 
      activeTab === 'ecommerce' ? updatedEcommerceIds.includes(f.id) : selectedEcommerceIds.includes(f.id)
    );
    
    // Combine with custom features
    const allSelected = [
      ...generalFeatures,
      ...ecommerceFeatures,
      ...customFeatures.map(f => ({
        ...f,
        selected: true,
        subsection: 'Custom Features',
        category: f.category || 'Custom',
        complexity: f.complexity || 1
      }))
    ];
    
    // Directly update the parent component
    onFeaturesChange(allSelected);
  };

  // Handle adding a custom feature
  const handleAddCustomFeature = () => {
    if (!customFeatureForm.title.trim()) return;
    
    // Create new custom feature with all required properties and a very distinctive ID
    const newFeature: Feature = {
      id: `custom-feature-${Date.now()}`,
      name: customFeatureForm.title,
      description: customFeatureForm.description || '',
      minHours: customFeatureForm.hours,
      maxHours: customFeatureForm.hours,
      subsection: 'Custom Features',
      selected: true, // Ensure the feature is marked as selected
      category: 'Custom', // Add a category for better identification
      complexity: 1 // Add a default complexity
    };
    
    // Add to custom features
    const updatedCustomFeatures = [...customFeatures, newFeature];
    setCustomFeatures(updatedCustomFeatures);
    
    // Get all selected features
    const generalFeatures = GENERAL_FEATURES.filter(f => selectedGeneralIds.includes(f.id));
    const ecommerceFeatures = ECOMMERCE_FEATURES.filter(f => selectedEcommerceIds.includes(f.id));
    
    // Combine all features
    const allSelected = [
      ...generalFeatures,
      ...ecommerceFeatures,
      ...updatedCustomFeatures.map(f => ({
        ...f,
        selected: true,
        subsection: 'Custom Features',
        category: f.category || 'Custom',
        complexity: f.complexity || 1
      }))
    ];
    
    // Directly update the parent component
    onFeaturesChange(allSelected);
    
    // Reset form
    setCustomFeatureForm({
      title: '',
      description: '',
      hours: 4
    });
    setShowCustomFeatureForm(false);
  };

  // Handle deleting a custom feature
  const handleDeleteCustomFeature = (featureId: string) => {
    // Update custom features state
    const updatedCustomFeatures = customFeatures.filter(f => f.id !== featureId);
    setCustomFeatures(updatedCustomFeatures);
    
    // Get all selected features
    const generalFeatures = GENERAL_FEATURES.filter(f => selectedGeneralIds.includes(f.id));
    const ecommerceFeatures = ECOMMERCE_FEATURES.filter(f => selectedEcommerceIds.includes(f.id));
    
    // Combine all features
    const allSelected = [
      ...generalFeatures,
      ...ecommerceFeatures,
      ...updatedCustomFeatures.map(f => ({
        ...f,
        selected: true,
        subsection: 'Custom Features',
        category: f.category || 'Custom',
        complexity: f.complexity || 1
      }))
    ];
    
    // Directly update the parent component
    onFeaturesChange(allSelected);
  };

  // Get features for current tab
  const currentFeatures = activeTab === 'general' ? GENERAL_FEATURES : ECOMMERCE_FEATURES;
  const selectedIds = activeTab === 'general' ? selectedGeneralIds : selectedEcommerceIds;

  // Group features by subsection
  const groupedFeatures = currentFeatures.reduce((acc, feature) => {
    const subsection = feature.subsection || 'General';
    if (!acc[subsection]) {
      acc[subsection] = [];
    }
    acc[subsection].push(feature);
    return acc;
  }, {} as Record<string, Feature[]>);

  // Sort subsections to ensure 'General' comes first
  const sortedSubsections = Object.keys(groupedFeatures).sort((a, b) => {
    if (a === 'General') return -1;
    if (b === 'General') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('general')}
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors",
            activeTab === 'general' 
              ? "border-b-2 border-primary text-primary" 
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          General
        </button>
        <button
          onClick={() => setActiveTab('ecommerce')}
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors",
            activeTab === 'ecommerce' 
              ? "border-b-2 border-primary text-primary" 
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          E-Commerce
        </button>
      </div>
      
      {/* Custom Features Section */}
      <div className="pt-2 mb-6">
        <h4 className="text-md font-medium mb-3 text-primary">Custom Features</h4>
        
        {/* Custom Features List */}
        {customFeatures.length > 0 && (
          <div className="mb-4">
            {customFeatures.map(feature => (
              <Card key={feature.id} className="overflow-hidden hover:border-primary/50 transition-colors mb-4">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Checkbox 
                      id={`feature-${feature.id}`}
                      checked={true}
                      disabled={true}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-base font-medium">
                            {feature.name}
                          </span>
                          <p className="text-sm text-muted-foreground mt-1">{feature.description}</p>
                          <div className="text-sm text-muted-foreground mt-2 inline-flex px-2 py-1 rounded-md bg-secondary">
                            Estimated: {feature.minHours} hours
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteCustomFeature(feature.id)}
                          className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                          title="Delete custom feature"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Add Custom Feature Button/Form */}
        {!showCustomFeatureForm ? (
          <Button 
            variant="outline" 
            onClick={() => setShowCustomFeatureForm(true)}
            className="w-full mb-2"
          >
            + Add Custom Feature
          </Button>
        ) : (
          <Card className="overflow-hidden mb-6">
            <CardContent className="p-4">
              <div className="space-y-4">
                <h4 className="font-medium">Add Custom Feature</h4>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Title</label>
                  <input
                    type="text"
                    value={customFeatureForm.title}
                    onChange={(e) => setCustomFeatureForm({...customFeatureForm, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    placeholder="Feature title"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <textarea
                    value={customFeatureForm.description}
                    onChange={(e) => setCustomFeatureForm({...customFeatureForm, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    rows={3}
                    placeholder="Feature description"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <label className="text-sm font-medium">Hours</label>
                    <span className="font-medium">{customFeatureForm.hours}</span>
                  </div>
                  
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={customFeatureForm.hours}
                    onChange={(e) => setCustomFeatureForm({...customFeatureForm, hours: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0</span>
                    <span>100</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={handleAddCustomFeature} disabled={!customFeatureForm.title.trim()}>
                    Add Feature
                  </Button>
                  <Button variant="outline" onClick={() => setShowCustomFeatureForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {customFeatures.length > 0 && !showCustomFeatureForm && (
          <p className="text-xs text-muted-foreground mb-4">
            Custom features are included in your project estimate and will be saved with your project.
          </p>
        )}
      </div>
      
      {/* Regular Features List */}
      <div className="pt-2">
        {sortedSubsections.map(subsection => (
          <div key={subsection} className="mb-6">
            {subsection !== 'General' && (
              <h4 className="text-md font-medium mb-3 text-primary">{subsection}</h4>
            )}
            {groupedFeatures[subsection].map(feature => (
              <Card key={feature.id} className="overflow-hidden hover:border-primary/50 transition-colors mb-4">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Checkbox 
                      id={`feature-${feature.id}`}
                      checked={selectedIds.includes(feature.id)}
                      onCheckedChange={(checked) => handleFeatureToggle(feature.id, checked === true)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={`feature-${feature.id}`}
                        className="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {feature.name}
                      </label>
                      <p className="text-sm text-muted-foreground mt-1">{feature.description}</p>
                      <div className="text-sm text-muted-foreground mt-2 inline-flex px-2 py-1 rounded-md bg-secondary">
                        Estimated: {feature.minHours === feature.maxHours 
                          ? `${feature.minHours} hours` 
                          : `${feature.minHours}-${feature.maxHours} hours`}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeatureSelection;